import axios, { AxiosError } from "axios"
import { AnyAction } from "redux"
import { Dispatch } from "../store.config"
import { RootState } from "../rootReducer"
import { ConfigureAxios } from "../../axios.config"
import { loginSuccessAction } from "./login.ducks"
import { FiduciaryFormData } from "../../components/RegistrationPanel"
import { cleanPhone } from "../../utils/phoneValidator"
import {
  registrationFailureAction,
  registrationFailureActionTakenFiduciaryName,
  registrationFailureActionWrongPhoneNumber,
} from "./fiduciary.ducks"

export interface UserFiduciaryPermissionItem {
  fiduciary_id: number
  fiduciary_name: string
  is_current: boolean
}

export interface SwitchFiduciaryResponse {
  fiduciary_id: number
  fiduciary_name: string
  switched_to_principal?: boolean
}

export interface UserFiduciaryPermissionsResponse {
  permissions: UserFiduciaryPermissionItem[]
  can_have_multiple_fiduciaries: boolean
}

const enum UserFiduciaryPermissionsActionsEnum {
  GET_PERMISSIONS_ATTEMPT = "USER_FIDUCIARY_PERMISSIONS/getPermissionsAttempt",
  GET_PERMISSIONS_SUCCESS = "USER_FIDUCIARY_PERMISSIONS/getPermissionsSuccess",
  GET_PERMISSIONS_FAILURE = "USER_FIDUCIARY_PERMISSIONS/getPermissionsFailure",

  SWITCH_FIDUCIARY_ATTEMPT = "USER_FIDUCIARY_PERMISSIONS/switchFiduciaryAttempt",
  SWITCH_FIDUCIARY_SUCCESS = "USER_FIDUCIARY_PERMISSIONS/switchFiduciarySuccess",
  SWITCH_FIDUCIARY_FAILURE = "USER_FIDUCIARY_PERMISSIONS/switchFiduciaryFailure",
  SWITCH_FIDUCIARY_RESET = "USER_FIDUCIARY_PERMISSIONS/switchFiduciaryReset",

  CREATE_ADDITIONAL_FIDUCIARY_ATTEMPT = "USER_FIDUCIARY_PERMISSIONS/createAdditionalFiduciaryAttempt",
  CREATE_ADDITIONAL_FIDUCIARY_SUCCESS = "USER_FIDUCIARY_PERMISSIONS/createAdditionalFiduciarySuccess",
  CREATE_ADDITIONAL_FIDUCIARY_FAILURE = "USER_FIDUCIARY_PERMISSIONS/createAdditionalFiduciaryFailure",
  CREATE_ADDITIONAL_FIDUCIARY_RESET = "USER_FIDUCIARY_PERMISSIONS/createAdditionalFiduciaryReset",
}

export const getPermissionsAttemptAction = () =>
  ({
    type: UserFiduciaryPermissionsActionsEnum.GET_PERMISSIONS_ATTEMPT,
  } as const)
export const getPermissionsSuccessAction = (
  payload: UserFiduciaryPermissionsResponse
) =>
  ({
    type: UserFiduciaryPermissionsActionsEnum.GET_PERMISSIONS_SUCCESS,
    payload,
  } as const)
export const getPermissionsFailureAction = () =>
  ({
    type: UserFiduciaryPermissionsActionsEnum.GET_PERMISSIONS_FAILURE,
  } as const)

export const switchFiduciaryAttemptAction = () =>
  ({
    type: UserFiduciaryPermissionsActionsEnum.SWITCH_FIDUCIARY_ATTEMPT,
  } as const)
export const switchFiduciarySuccessAction = () =>
  ({
    type: UserFiduciaryPermissionsActionsEnum.SWITCH_FIDUCIARY_SUCCESS,
  } as const)
export const switchFiduciaryFailureAction = () =>
  ({
    type: UserFiduciaryPermissionsActionsEnum.SWITCH_FIDUCIARY_FAILURE,
    withToast: true,
    toasterType: "error",
    message: {
      titleKey: "fiduciary.switch.error.title",
      bodyKey: "fiduciary.switch.error.body",
    },
  } as const)
export const switchFiduciaryResetAction = () =>
  ({
    type: UserFiduciaryPermissionsActionsEnum.SWITCH_FIDUCIARY_RESET,
  } as const)

export const createAdditionalFiduciaryAttemptAction = () =>
  ({
    type: UserFiduciaryPermissionsActionsEnum.CREATE_ADDITIONAL_FIDUCIARY_ATTEMPT,
  } as const)
export const createAdditionalFiduciarySuccessAction = () =>
  ({
    type: UserFiduciaryPermissionsActionsEnum.CREATE_ADDITIONAL_FIDUCIARY_SUCCESS,
  } as const)
export const createAdditionalFiduciaryFailureAction = () =>
  ({
    type: UserFiduciaryPermissionsActionsEnum.CREATE_ADDITIONAL_FIDUCIARY_FAILURE,
  } as const)
export const createAdditionalFiduciaryResetAction = () =>
  ({
    type: UserFiduciaryPermissionsActionsEnum.CREATE_ADDITIONAL_FIDUCIARY_RESET,
  } as const)

type UserFiduciaryPermissionsActionsType = ReturnType<
  | typeof getPermissionsAttemptAction
  | typeof getPermissionsSuccessAction
  | typeof getPermissionsFailureAction
  | typeof switchFiduciaryAttemptAction
  | typeof switchFiduciarySuccessAction
  | typeof switchFiduciaryFailureAction
  | typeof switchFiduciaryResetAction
  | typeof createAdditionalFiduciaryAttemptAction
  | typeof createAdditionalFiduciarySuccessAction
  | typeof createAdditionalFiduciaryFailureAction
  | typeof createAdditionalFiduciaryResetAction
>

export interface UserFiduciaryPermissionsState {
  permissions: UserFiduciaryPermissionItem[]
  canHaveMultipleFiduciaries: boolean
  getPermissionsStatus: "IDLE" | "LOADING" | "SUCCESS" | "ERROR"
  switchStatus: "IDLE" | "LOADING" | "SUCCESS" | "ERROR"
  createAdditionalStatus: "IDLE" | "LOADING" | "SUCCESS" | "ERROR"
}

export const userFiduciaryPermissionsInitialState: UserFiduciaryPermissionsState =
  {
    permissions: [],
    canHaveMultipleFiduciaries: false,
    getPermissionsStatus: "IDLE",
    switchStatus: "IDLE",
    createAdditionalStatus: "IDLE",
  }

export function userFiduciaryPermissionsReducer(
  state = userFiduciaryPermissionsInitialState,
  action: UserFiduciaryPermissionsActionsType
): UserFiduciaryPermissionsState {
  switch (action.type) {
    case UserFiduciaryPermissionsActionsEnum.GET_PERMISSIONS_ATTEMPT:
      return { ...state, getPermissionsStatus: "LOADING" }
    case UserFiduciaryPermissionsActionsEnum.GET_PERMISSIONS_SUCCESS:
      return {
        ...state,
        getPermissionsStatus: "SUCCESS",
        permissions: action.payload.permissions,
        canHaveMultipleFiduciaries:
          action.payload.can_have_multiple_fiduciaries,
      }
    case UserFiduciaryPermissionsActionsEnum.GET_PERMISSIONS_FAILURE:
      return {
        ...state,
        getPermissionsStatus: "ERROR",
        canHaveMultipleFiduciaries: false,
        permissions: [],
      }

    case UserFiduciaryPermissionsActionsEnum.SWITCH_FIDUCIARY_ATTEMPT:
      return { ...state, switchStatus: "LOADING" }
    case UserFiduciaryPermissionsActionsEnum.SWITCH_FIDUCIARY_SUCCESS:
      return { ...state, switchStatus: "SUCCESS" }
    case UserFiduciaryPermissionsActionsEnum.SWITCH_FIDUCIARY_FAILURE:
      return { ...state, switchStatus: "ERROR" }
    case UserFiduciaryPermissionsActionsEnum.SWITCH_FIDUCIARY_RESET:
      return { ...state, switchStatus: "IDLE" }

    case UserFiduciaryPermissionsActionsEnum.CREATE_ADDITIONAL_FIDUCIARY_ATTEMPT:
      return { ...state, createAdditionalStatus: "LOADING" }
    case UserFiduciaryPermissionsActionsEnum.CREATE_ADDITIONAL_FIDUCIARY_SUCCESS:
      return { ...state, createAdditionalStatus: "SUCCESS" }
    case UserFiduciaryPermissionsActionsEnum.CREATE_ADDITIONAL_FIDUCIARY_FAILURE:
      return { ...state, createAdditionalStatus: "ERROR" }
    case UserFiduciaryPermissionsActionsEnum.CREATE_ADDITIONAL_FIDUCIARY_RESET:
      return { ...state, createAdditionalStatus: "IDLE" }

    default:
      return { ...state }
  }
}

export const getUserFiduciaryPermissionsThunk =
  () => (dispatch: Dispatch<AnyAction>) => {
    dispatch(getPermissionsAttemptAction())
    return axios
      .get<UserFiduciaryPermissionsResponse>(
        `/users/user_fiduciary_permissions.json`,
        {
          headers: { Authorization: String(localStorage.getItem("token")) },
        }
      )
      .then(({ data }) => {
        dispatch(
          getPermissionsSuccessAction(
            normalizeUserFiduciaryPermissionsResponse(data)
          )
        )
      })
      .catch((error: AxiosError) => {
        if (error.response?.status === 403) {
          dispatch(
            getPermissionsSuccessAction({
              permissions: [],
              can_have_multiple_fiduciaries: false,
            })
          )
          return
        }
        dispatch(getPermissionsFailureAction())
      })
  }

const normalizeUserFiduciaryPermissionsResponse = (
  data: UserFiduciaryPermissionsResponse | UserFiduciaryPermissionItem[]
): UserFiduciaryPermissionsResponse => {
  if (Array.isArray(data)) {
    return {
      permissions: data,
      can_have_multiple_fiduciaries: data.length > 0,
    }
  }

  return {
    permissions: data.permissions ?? [],
    can_have_multiple_fiduciaries: Boolean(data.can_have_multiple_fiduciaries),
  }
}

export const createAdditionalFiduciaryThunk =
  (fiduciaryFormData: FiduciaryFormData) =>
  (dispatch: Dispatch<AnyAction>, getState: () => RootState) => {
    if (!getState().userFiduciaryPermissions.canHaveMultipleFiduciaries) {
      return Promise.resolve()
    }

    dispatch(createAdditionalFiduciaryAttemptAction())
    const fiduciary = getState().fiduciary

    const currentToken = localStorage.getItem("token")
    const principalToken = localStorage.getItem("principalToken")
    if (currentToken && !principalToken) {
      localStorage.setItem("principalToken", currentToken)
    }
    const authToken = principalToken || currentToken

    const requestPayload = {
      fiduciary: {
        fiduciary_name: fiduciaryFormData.fiduciaryName,
        street_infos: fiduciaryFormData.address,
        city: fiduciaryFormData.city,
        postal_code: fiduciaryFormData.postalCode,
        phone: cleanPhone(fiduciaryFormData.phone),
        origin: fiduciaryFormData.localisation.value,
        accounting_software: fiduciaryFormData.accounting_software.value,
        how_did_you_hear: fiduciaryFormData.discovery.value,
        identification_number: fiduciary.siren,
        code_ape: fiduciary.code_ape,
        fiduciary_friend: fiduciary.referer,
      },
    }

    return axios
      .post<SwitchFiduciaryResponse>(
        `/users/user_fiduciary_permissions.json`,
        requestPayload,
        { headers: { Authorization: String(authToken) } }
      )
      .then((response) => {
        const token = response.headers.authorization
        if (!token) {
          dispatch(createAdditionalFiduciaryFailureAction())
          return dispatch(
            registrationFailureAction({
              name: "Error",
              message: "missing token",
            } as AxiosError)
          )
        }
        localStorage.setItem("token", token)
        ConfigureAxios()
        dispatch(createAdditionalFiduciarySuccessAction())
        dispatch(loginSuccessAction())
      })
      .catch((e: AxiosError) => {
        dispatch(createAdditionalFiduciaryFailureAction())
        const errorMessage = (e.response?.data as { error?: string })?.error
        if (
          typeof errorMessage === "string" &&
          errorMessage.includes("fiduciary_name")
        ) {
          return dispatch(registrationFailureActionTakenFiduciaryName())
        }
        if (
          typeof errorMessage === "string" &&
          errorMessage.includes("phone")
        ) {
          return dispatch(registrationFailureActionWrongPhoneNumber())
        }
        return dispatch(registrationFailureAction(e))
      })
  }

export const switchFiduciaryThunk =
  (fiduciary_id: number) => (dispatch: Dispatch<AnyAction>) => {
    dispatch(switchFiduciaryAttemptAction())

    const currentToken = localStorage.getItem("token")
    const principalToken = localStorage.getItem("principalToken")

    if (currentToken && !principalToken) {
      localStorage.setItem("principalToken", currentToken)
    }

    const authToken = principalToken || currentToken

    return axios
      .post(
        `/users/switch_fiduciary.json`,
        { fiduciary_id },
        { headers: { Authorization: String(authToken) } }
      )
      .then((response) => {
        const token = response.headers.authorization
        if (!token) {
          localStorage.removeItem("principalToken")
          dispatch(switchFiduciaryFailureAction())
          return
        }
        localStorage.setItem("token", token)
        if ((response.data as SwitchFiduciaryResponse)?.switched_to_principal) {
          localStorage.removeItem("principalToken")
        }
        ConfigureAxios()
        dispatch(switchFiduciarySuccessAction())
        dispatch(loginSuccessAction())
        window.location.replace("/rnb")
      })
      .catch((_e: AxiosError) => {
        localStorage.removeItem("principalToken")
        dispatch(switchFiduciaryFailureAction())
      })
  }

export const switchBackToPrincipalThunk =
  () => (dispatch: Dispatch<AnyAction>) => {
    const principalToken = localStorage.getItem("principalToken")
    if (!principalToken) return

    localStorage.setItem("token", principalToken)
    localStorage.removeItem("principalToken")
    ConfigureAxios()
    dispatch(loginSuccessAction())
    window.location.replace("/rnb")
  }
